import { label } from '../../lib/labels'

/**
 * Pure lifecycle-derivation logic for the Case Lifecycle Progress Tracker (film credit lifecycle
 * polish, GA/NY/LA). Deliberately data-only and jurisdiction-agnostic (CLAUDE.md §2 "config, not
 * code" — no `if (jurisdiction === ...)`/`if (program === ...)` branch anywhere in this file): every
 * stage is derived from generic fields (`ProgramFiling.currentState`/`submissionOutcome`,
 * `CreditInstrument.certifiedAmount`, `TransferTxn.salePrice`, `Dossier.status`, qualified-spend
 * totals) that already exist identically for a Georgia, New York, or Louisiana film Case — never a
 * per-state table/column/branch. No fetch happens here; every input is data the caller (currently
 * `OverviewPanel`) already resolved.
 */

export type LifecycleStepId =
  | 'assessment'
  | 'qualifiedSpend'
  | 'certified'
  | 'monetized'
  | 'dossierFinalized'
  | 'filedOutcome'

export type LifecycleStepStatus = 'done' | 'current' | 'upcoming'

export interface LifecycleStep {
  id: LifecycleStepId
  label: string
  status: LifecycleStepStatus
  /** A one-line, real-data detail (certified amount, transfer net, dossier version, outcome) —
   * populated ONLY for `done` steps (AC: "a one-line detail per completed step"); `null` for
   * `current`/`upcoming` steps, never a fabricated placeholder. */
  detail: string | null
}

/** The Case-scoped subset of `ProgramFilingDetailView` this module actually reads — kept narrower
 * than the full wire type so this module has no import-time coupling to `types/programFilingDetail`
 * (easier to unit test, and honest about exactly which fields drive the derivation). */
export interface LifecycleFilingInput {
  submissionOutcome: string | null
  filedAt: string | null
  approvedAmount: number | null
  approvedCurrency: string | null
}

/** The subset of `CreditInstrumentView` this module reads. */
export interface LifecycleCreditInstrumentInput {
  certifiedAmount: number
  currency: string
}

/** The subset of `TransferView` this module reads. `salePrice` is already the net proceeds figure
 * (the wire `discount` field is a *rate*, not a currency amount subtracted from `salePrice` — see
 * `OverviewPanel`'s own `transferredTotal` convention, which this mirrors rather than re-deriving a
 * second "net" figure from `amount`/`discount`). */
export interface LifecycleTransferInput {
  salePrice: number
  currency: string | null
}

/** The subset of `DossierSummaryView` this module reads. */
export interface LifecycleDossierInput {
  status: string
  version: number
  finalizedAt: string | null
}

export interface LifecycleCaseInput {
  filings: LifecycleFilingInput[]
  qualifiedSpendTotal: number | null
  qualifiedSpendCurrency: string | null
  certifiedInstruments: LifecycleCreditInstrumentInput[]
  transfers: LifecycleTransferInput[]
  dossiers: LifecycleDossierInput[]
}

const STEP_ORDER: LifecycleStepId[] = [
  'assessment',
  'qualifiedSpend',
  'certified',
  'monetized',
  'dossierFinalized',
  'filedOutcome',
]

export const STEP_LABEL: Record<LifecycleStepId, string> = {
  assessment: 'Assessment',
  qualifiedSpend: 'Qualified spend',
  certified: 'Certified',
  monetized: 'Monetized',
  dossierFinalized: 'Dossier finalized',
  filedOutcome: 'Filed / Outcome',
}

function formatAmount(amount: number, currency: string | null): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency ?? 'USD' }).format(amount)
  } catch {
    return `${currency ?? ''} ${amount.toFixed(2)}`.trim()
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString()
}

/**
 * Derives the six-stage lifecycle stepper (Assessment -> Qualified spend -> Certified -> Monetized
 * -> Dossier finalized -> Filed/Outcome) purely from data the Case already resolves. Each stage's
 * `done` flag comes from its own real-data criterion; the stepper stays visually monotonic (the
 * first not-yet-done stage in order is `current`, everything after it is `upcoming` even if that
 * stage's own criterion happened to already be true — e.g. an out-of-order data state never renders
 * as a skip-ahead stepper) so this is always a single linear "you are here" read, never a
 * jurisdiction/program-specific branch.
 */
export function deriveLifecycle(input: LifecycleCaseInput): LifecycleStep[] {
  const filings = input.filings ?? []
  const certifiedInstruments = input.certifiedInstruments ?? []
  const transfers = input.transfers ?? []
  const dossiers = input.dossiers ?? []

  const qualifiedSpendTotal = input.qualifiedSpendTotal ?? 0
  const qualifiedSpendDone = qualifiedSpendTotal > 0

  const certifiedTotal = certifiedInstruments.reduce((sum, i) => sum + i.certifiedAmount, 0)
  const certifiedDone = certifiedTotal > 0
  const certifiedCurrency = certifiedInstruments[0]?.currency ?? null

  const monetizedTotal = transfers.reduce((sum, t) => sum + t.salePrice, 0)
  const monetizedDone = monetizedTotal > 0
  const monetizedCurrency = transfers[0]?.currency ?? certifiedCurrency

  const finalDossiers = dossiers
    .filter((d) => d.status === 'final')
    .sort((a, b) => b.version - a.version)
  const latestFinalDossier = finalDossiers[0] ?? null
  const dossierDone = latestFinalDossier !== null

  const outcomeFiling = filings.find((f) => f.submissionOutcome !== null) ?? null
  const filedOutcomeDone = outcomeFiling !== null

  // Assessment is the entry stage: done once the case has produced a real ProgramFiling or already
  // has spend recorded (both imply the initial eligibility/assessment work is behind it); otherwise
  // it is the stage currently in progress.
  const assessmentDone = filings.length > 0 || qualifiedSpendDone

  const doneFlag: Record<LifecycleStepId, boolean> = {
    assessment: assessmentDone,
    qualifiedSpend: qualifiedSpendDone,
    certified: certifiedDone,
    monetized: monetizedDone,
    dossierFinalized: dossierDone,
    filedOutcome: filedOutcomeDone,
  }

  const firstNotDoneIndex = STEP_ORDER.findIndex((id) => !doneFlag[id])
  const currentIndex = firstNotDoneIndex === -1 ? STEP_ORDER.length : firstNotDoneIndex

  function detailFor(id: LifecycleStepId): string | null {
    switch (id) {
      case 'assessment':
        return filings.length > 0
          ? `${filings.length} program filing${filings.length === 1 ? '' : 's'} opened.`
          : 'Qualified spend recorded.'
      case 'qualifiedSpend':
        return `Qualified spend ${formatAmount(qualifiedSpendTotal, input.qualifiedSpendCurrency)} recorded.`
      case 'certified':
        return `Certified ${formatAmount(certifiedTotal, certifiedCurrency)}.`
      case 'monetized':
        return `Transfer net ${formatAmount(monetizedTotal, monetizedCurrency)}.`
      case 'dossierFinalized': {
        if (!latestFinalDossier) return null
        const dateSuffix = latestFinalDossier.finalizedAt ? ` (${formatDate(latestFinalDossier.finalizedAt)})` : ''
        return `Dossier v${latestFinalDossier.version} finalized${dateSuffix}.`
      }
      case 'filedOutcome': {
        if (!outcomeFiling) return null
        const outcomeLabel = label('submissionOutcome', outcomeFiling.submissionOutcome)
        const amountSuffix =
          outcomeFiling.approvedAmount !== null
            ? ` · ${formatAmount(outcomeFiling.approvedAmount, outcomeFiling.approvedCurrency)}`
            : ''
        return `${outcomeLabel}${amountSuffix}.`
      }
      default:
        return null
    }
  }

  return STEP_ORDER.map((id, index) => {
    const status: LifecycleStepStatus = index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'upcoming'
    return {
      id,
      label: STEP_LABEL[id],
      status,
      detail: status === 'done' ? detailFor(id) : null,
    }
  })
}
